import React, { useEffect, useMemo, useState } from "react";
import { translateUi } from "./i18n/index";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { AlertTriangle, Printer, X } from "./icons";
import { GbrModalBackdrop } from "./GbrModalBackdrop";
import { buildEmployeeBrochureDataProtectionNotice } from "./dataProtectionPrintNotice";
import { printEmployeeBrochure, type BrochureInput, type BrochureRow } from "./employeeProfileBrochure";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function sameText(a: unknown, b: unknown): boolean {
  const left = String(a ?? "").trim().toLowerCase();
  const right = String(b ?? "").trim().toLowerCase();
  return !!left && left === right;
}

/** Signature already loaded on the open employee record, if that record belongs to the producer. */
function signatureFromOpenProfile(
  employee: BrochureRow,
  producerEmail?: string,
  producerEmployeeId?: string,
  producedBy?: string,
): string | null {
  const onRecord = String(employee.signature_url ?? "").trim();
  if (!onRecord) return null;
  if (sameText(producerEmail, employee.email)) return onRecord;
  if (sameText(producerEmployeeId, employee.id)) return onRecord;
  if (sameText(producedBy, employee.full_name)) return onRecord;
  return null;
}

type BrochureFinish = "colour" | "mono" | "dark";

type SignatureState = "loading" | "ready" | "none" | "error";

const FINISH_OPTIONS: { id: BrochureFinish; label: string; hint: string }[] = [
  {
    id: "colour",
    label: "Full colour brochure",
    hint: "Branded artwork on every page. Best for saving as a PDF or sharing on screen.",
  },
  {
    id: "mono",
    label: "Print friendly",
    hint: "Plain black on white with no artwork or colour fills, to save ink on paper.",
  },
  {
    id: "dark",
    label: "Dark mode",
    hint: "Best for on-screen reading. Not recommended for printing.",
  },
];

/**
 * "Print full profile" action. Available to anyone who can view the profile, but gated behind a
 * mandatory data-protection acknowledgement because the brochure aggregates the entire record —
 * including health and incident data — into a single portable document.
 */
export function EmployeeProfileBrochureButton({
  employee,
  documents,
  employeeCycles,
  cycleEvents,
  safetyBriefs,
  isDarkMode,
  producedBy,
  producerEmail,
  producerEmployeeId,
  companyHeaders,
}: {
  employee: BrochureRow;
  documents?: BrochureRow[];
  employeeCycles?: BrochureRow[];
  cycleEvents?: BrochureRow[];
  safetyBriefs?: BrochureRow[];
  isDarkMode?: boolean;
  producedBy?: string;
  /** Signed-in user's email — used to take the signature from the open profile when it is theirs. */
  producerEmail?: string;
  /** Linked employee id on the signed-in user, when known. */
  producerEmployeeId?: string;
  /** Tenant + actor headers, matching the rest of the profile's fetches. */
  companyHeaders?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [finish, setFinish] = useState<BrochureFinish>("colour");
  const [applySignature, setApplySignature] = useState(true);
  const [signature, setSignature] = useState<string | null>(null);
  const [signatureState, setSignatureState] = useState<SignatureState>("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notice = useMemo(() => buildEmployeeBrochureDataProtectionNotice(), []);

  const ownSignature = signatureFromOpenProfile(employee, producerEmail, producerEmployeeId, producedBy);

  useEffect(() => {
    if (!open) return;
    setAcknowledged(false);
    setError(null);
    setFinish("colour");
    setApplySignature(true);
    if (ownSignature) {
      setSignature(ownSignature);
      setSignatureState("ready");
    }
  }, [open, ownSignature]);

  /*
   * The producer's own signature, so the declaration can be pre-signed. Prefer the open profile when
   * it is theirs (already in memory). Still call the API for producers who are printing someone else.
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    if (!ownSignature) setSignatureState("loading");
    (async () => {
      try {
        const res = await fetch("/api/my-signature", {
          headers: { ...(companyHeaders ?? {}) },
          cache: "no-store",
        });
        const payload = (await res.json().catch(() => null)) as
          | { signature_url?: string | null; reason?: string | null; error?: string | null }
          | null;
        if (cancelled) return;
        const fromApi = payload?.signature_url?.trim() || null;
        const found = fromApi || ownSignature || null;
        setSignature(found);
        if (found) setSignatureState("ready");
        else if (!res.ok) setSignatureState("error");
        else setSignatureState("none");
      } catch {
        if (cancelled) return;
        if (ownSignature) {
          setSignature(ownSignature);
          setSignatureState("ready");
        } else {
          setSignature(null);
          setSignatureState("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyHeaders, ownSignature]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);

  const run = async () => {
    if (busy || !acknowledged) return;
    setBusy(true);
    setError(null);
    try {
      const input: BrochureInput = {
        employee,
        documents,
        employeeCycles,
        cycleEvents,
        safetyBriefs,
        dark: finish === "dark",
        printFriendly: finish === "mono",
        producedBy,
        producedBySignature: applySignature ? signature || ownSignature || undefined : undefined,
      };
      await printEmployeeBrochure(input);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center px-4 py-2 rounded-xl text-sm font-medium cursor-pointer select-none",
          "transition-all duration-150 hover:shadow-md hover:-translate-y-px",
          "active:translate-y-0 active:scale-[0.97] active:shadow-sm",
          isDarkMode
            ? "bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-800"
            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50",
        )}
        title={translateUi("Produce a brochure-style PDF of the complete employee profile")}
      >
        <Printer className="w-4 h-4 mr-2" />
        {translateUi("Print Full Profile")}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => {
            if (!busy) setOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="brochure-print-title"
            className={cn(
              "relative w-full max-w-xl overflow-hidden rounded-t-3xl border shadow-2xl sm:rounded-3xl",
              isDarkMode
                ? "border-slate-700 bg-slate-900 text-slate-100"
                : "border-slate-200 bg-white text-slate-900",
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <GbrModalBackdrop opacity={isDarkMode ? 0.07 : 0.05} />
            <div className="relative">
              <div className={cn("px-5 pt-5 sm:px-6 sm:pt-6", isDarkMode ? "bg-amber-950/35" : "bg-amber-50/90")}>
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      "mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
                      isDarkMode ? "bg-amber-900/60 text-amber-200" : "bg-amber-100 text-amber-800",
                    )}
                  >
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 id="brochure-print-title" className="text-lg font-bold leading-snug">
                      {translateUi("Before you print")}
                    </h3>
                    <p className={cn("mt-0.5 text-sm font-medium", isDarkMode ? "text-amber-200/85" : "text-amber-800")}>
                      {notice.title}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    disabled={busy}
                    className={cn(
                      "rounded-lg p-2 transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                      isDarkMode ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-white/80",
                    )}
                    aria-label={translateUi("Close")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-4 space-y-2 pb-5">
                  <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                    {notice.body}
                  </p>
                  <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    {translateUi(
                      "Your browser's print dialog will open. Choose your printer, or select \u201CSave as PDF\u201D as the destination to keep a digital copy.",
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-3 px-5 pt-4 sm:px-6">
                <label
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm",
                    isDarkMode ? "border-slate-700 bg-slate-950/60" : "border-slate-200 bg-slate-50",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
                  />
                  <span>
                    {translateUi(
                      "I confirm I have a lawful basis and a business need to produce this record, and I will handle, store and dispose of it in line with {{law}}.",
                      { law: notice.law },
                    )}
                  </span>
                </label>

                <fieldset className="space-y-2">
                  <legend className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    {translateUi("Finish")}
                  </legend>
                  {FINISH_OPTIONS.filter((option) => option.id !== "dark" || isDarkMode).map((option) => (
                    <label
                      key={option.id}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm",
                        finish === option.id
                          ? isDarkMode
                            ? "border-indigo-400/60 bg-indigo-500/10"
                            : "border-indigo-300 bg-indigo-50/70"
                          : isDarkMode
                            ? "border-slate-700 bg-slate-950/60"
                            : "border-slate-200 bg-white",
                      )}
                    >
                      <input
                        type="radio"
                        name="brochure-finish"
                        checked={finish === option.id}
                        onChange={() => setFinish(option.id)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-600"
                      />
                      <span>
                        {translateUi(option.label)}
                        <span className={cn("mt-0.5 block text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                          {translateUi(option.hint)}
                        </span>
                      </span>
                    </label>
                  ))}
                </fieldset>

                {signatureState === "ready" && signature ? (
                  <label
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm",
                      isDarkMode ? "border-slate-700 bg-slate-950/60" : "border-slate-200 bg-white",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={applySignature}
                      onChange={(event) => setApplySignature(event.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-600"
                    />
                    <span>
                      {translateUi("Apply my signature to the declaration")}
                      <span className={cn("mt-1 block", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                        <img
                          src={signature}
                          alt={translateUi("Your signature")}
                          className={cn(
                            "h-10 max-w-[200px] rounded border object-contain px-2 py-1",
                            isDarkMode ? "border-slate-700 bg-white" : "border-slate-200 bg-white",
                          )}
                        />
                      </span>
                    </span>
                  </label>
                ) : (
                  <p
                    className={cn(
                      "rounded-xl border p-3 text-sm",
                      isDarkMode ? "border-slate-700 bg-slate-950/60 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500",
                    )}
                  >
                    {signatureState === "loading"
                      ? translateUi("Checking your profile for a signature…")
                      : signatureState === "error"
                        ? translateUi(
                            "Your signature could not be loaded, so the declaration will be left blank to sign by hand.",
                          )
                        : translateUi(
                            "No signature is stored on your profile, so the declaration will be left blank to sign by hand.",
                          )}
                  </p>
                )}

                {error ? (
                  <p className={cn("text-sm font-medium", isDarkMode ? "text-rose-300" : "text-rose-600")}>{error}</p>
                ) : null}
              </div>

              <div className="flex flex-col-reverse gap-2 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  className={cn(
                    "min-h-[44px] rounded-xl px-4 text-sm font-semibold disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                    isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100",
                  )}
                >
                  {translateUi("Cancel")}
                </button>
                <button
                  type="button"
                  disabled={busy || !acknowledged || signatureState === "loading"}
                  onClick={() => void run()}
                  className="flex min-h-[44px] items-center justify-center rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-500 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  {busy ? translateUi("Preparing…") : translateUi("Print / Save as PDF")}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
